import { AsyncAuthWrapper } from './frontend-auth/auth-wrapper';
import { parseSearchArgs } from './frontend-auth/handover-token';
import { navigateToLogin, restoreLoginDestination } from './login-session';
import { AccessDeniedError, AuthenticationRequiredError, PermissionsUnavailableError } from './editor-errors';
import { closeErrorPopup, requestCloseTab, showErrorPopup } from './editor-popup';

import { Editor } from './components/Editor';

/**
 * The elements that make up the editor page.
 */
export interface UIElements {
    main: HTMLElement,

    /** The canvas for the redaction box editor */
    display: HTMLCanvasElement,

    /** The container div for the template editor */
    templatingDisplay: HTMLDivElement,

    /** The embed element to load PDFs in */
    pdfDisplay: HTMLEmbedElement,

    /** Document filters and list, in the left sidebar */
    documents: {
        div: HTMLDivElement;
        /** Button that says "Refresh", next to documents */
        refreshButton: HTMLButtonElement;

        /** The "Results" heading, above recentDocsContainer */
        resultsHeader: HTMLElement;
        /** The container for the document list */
        recentDocsContainer: HTMLDivElement;
    },

    /** Menu on the left of the page */
    smallMenu: {
        div: HTMLDivElement;
        closeMenuButton: HTMLButtonElement;
        /** Button to open the sidebar. Not really in the smallMenu, but it goes where the menu was when it's hidden. */
        openMenuButton: HTMLButtonElement;
        /** Button to create a new redaction box */
        newRedactionButton: HTMLButtonElement;
        /** Button for "show redacted file only" */
        redactedButton: HTMLButtonElement;
        /** Button for "show unredacted file" */
        unredactedButton: HTMLButtonElement;
    }

    /** Toolbar in the bottom right, primarily containing the submit button */
    toolbar: {
        div: HTMLDivElement,
        /** Button that says "Submit" */
        submitButton: HTMLButtonElement,
    },
}

const ui: UIElements = {
    main: document.querySelector("main")!,

    display: document.getElementById("display") as HTMLCanvasElement,
    templatingDisplay: document.getElementById("templating-display") as HTMLDivElement,
    pdfDisplay: document.getElementById("embed-pdf") as HTMLEmbedElement,

    documents: {
        div: document.getElementById("recent-docs") as HTMLDivElement,
        refreshButton: document.getElementById("refresh-button") as HTMLButtonElement,
        resultsHeader: document.getElementById("results-header") as HTMLDivElement,
        recentDocsContainer: document.getElementById("recent-docs-container") as HTMLDivElement,
    },

    smallMenu: {
        div: document.getElementById("small-menu") as HTMLDivElement,
        closeMenuButton: document.getElementById("close-menu-button") as HTMLButtonElement,
        openMenuButton: document.getElementById("open-menu-button") as HTMLButtonElement,
        newRedactionButton: document.getElementById("new-redaction") as HTMLButtonElement,
        unredactedButton: document.getElementById("unredacted-button") as HTMLButtonElement,
        redactedButton: document.getElementById("redacted-button") as HTMLButtonElement,
    },

    toolbar: {
        div: document.getElementById("toolbar") as HTMLDivElement,
        submitButton: document.getElementById("submit-button") as HTMLButtonElement,
    },
};

interface Filters {
    CandidateId: string;
    JobTitle: string;
    CandidateName: string;
    JobId: string;
}

const filters: Filters = {
    CandidateId: '',
    JobTitle: '',
    CandidateName: '',
    JobId: '',
};

function sleep(duration: number): Promise<void> {
    return new Promise(resolve => setTimeout(() => resolve(), duration));
}

async function hidePageQuick() {
    ui.main.classList.add('hidden');
}

async function showPage() {
    ui.main.classList.remove('hidden');
    ui.main.classList.remove('hidden-smooth');
}

/**
 * Hide the sidebar.
 *
 * @param hard - if true, the button to show the sidebar again is removed
 */
async function hideSidebar(hard: boolean = false) {
    ui.main.classList.add('hidden-smooth');
    ui.smallMenu.div.classList.add('hidden-smooth');
    await sleep(320);
    ui.main.style.paddingLeft = '50px';
    ui.main.style.paddingRight = '50px';
    ui.smallMenu.closeMenuButton.classList.add('hidden');
    ui.documents.div.classList.add('hidden');
    ui.main.classList.remove('hidden');
    ui.main.classList.remove('hidden-smooth');
    if (hard) ui.smallMenu.openMenuButton.classList.add('hidden');
    else ui.smallMenu.openMenuButton.classList.remove('hidden');
    ui.smallMenu.div.classList.remove('hidden-smooth');
}

async function openSidebar() {
    if (!args.sat) void editor?.updateList(filters, true).catch(error =>
        showLoadError(error, 'list', () => editor.updateList(filters, true)));
    ui.main.classList.add('hidden-smooth');
    ui.smallMenu.div.classList.add('hidden-smooth');
    await sleep(320);
    ui.main.style.paddingLeft = 'unset';
    ui.main.style.paddingRight = 'unset';
    ui.smallMenu.openMenuButton.classList.add('hidden');
    ui.documents.div.classList.remove('hidden');
    ui.main.classList.remove('hidden');
    ui.main.classList.remove('hidden-smooth');
    ui.smallMenu.closeMenuButton.classList.remove('hidden');
    ui.smallMenu.div.classList.remove('hidden-smooth');
}

ui.smallMenu.closeMenuButton.addEventListener('click', () => hideSidebar());
ui.smallMenu.openMenuButton.addEventListener('click', openSidebar);

function closeOverlays() {
    document.getElementById('overlay')?.classList.remove('active');
    document.getElementById('success-popup')?.classList.remove('active');
    closeErrorPopup();
}

document.querySelectorAll('.close-overlay')
    .forEach(elem => elem.addEventListener('click', closeOverlays));

function configureCloseTab(integrationSettingsId: number) {
    document.getElementById('close-tab-button')?.classList.toggle('hidden', integrationSettingsId !== 118);
}

document.getElementById('close-tab-button')?.addEventListener('click', () => {
    requestCloseTab(document.getElementById('close-tab-message')!);
});

export function activateOverlay() {
    closeOverlays();
    document.getElementById('overlay')?.classList.add('active');
}
export function showSuccess() {
    document.getElementById('close-tab-message')?.classList.add('hidden');
    activateOverlay();
    document.getElementById('success-popup')?.classList.add('active');
}

export function showSubmissionError(error: unknown, retry?: () => Promise<void>) {
    closeOverlays();
    if (error instanceof AuthenticationRequiredError) return;
    const back = { label: 'Back to document' };
    if (error instanceof AccessDeniedError) {
        showErrorPopup({
            title: 'Your changes haven’t been submitted',
            message: 'You no longer have permission to edit this document. Your edits are still on this page.',
            actions: [back],
        });
    } else if (error instanceof PermissionsUnavailableError) {
        showErrorPopup({
            title: 'We can’t check your permissions',
            message: 'Your changes haven’t been submitted. Your edits are still on this page. Please try again shortly.',
            actions: retry ? [{
                label: 'Try again',
                run: () => { void retry().catch(error => showSubmissionError(error, retry)); },
            }, back] : [back],
        });
    } else {
        console.error(error);
        showErrorPopup({
            title: 'We couldn’t confirm your submission',
            message: 'Keep this page open and check the document before retrying. Your edits are still on this page.',
            actions: [back],
        });
    }
}

function documentUnavailable(title: string, message: string) {
    showErrorPopup({
        title,
        message,
        actions: editor && !args.sat ? [{ label: 'View available documents', run: () => { void openSidebar(); } }] : [{ label: 'OK' }],
        closeTab: true,
    });
}

export function showLoadError(error: unknown, target: 'document' | 'list' = 'document', retry?: () => Promise<unknown>) {
    closeOverlays();
    if (error instanceof AuthenticationRequiredError) return;
    if (error instanceof AccessDeniedError) {
        if (target === 'document') {
            documentUnavailable('You don’t have access to this document', 'Your permissions for this job don’t allow you to edit this document.');
        } else {
            showErrorPopup({
                title: 'You don’t have access to the candidate list',
                message: 'Your current permissions don’t allow you to view this candidate list.',
                actions: [{ label: 'OK' }],
                closeTab: true,
            });
        }
        return;
    }
    const permissionsUnavailable = error instanceof PermissionsUnavailableError;
    if (!permissionsUnavailable) console.error(error);
    const subject = target === 'document' ? 'document' : 'candidate list';
    showErrorPopup({
        title: permissionsUnavailable ? 'We can’t check your permissions' : `We couldn’t load this ${subject}`,
        message: permissionsUnavailable
            ? `The ${subject} couldn’t be loaded because we can’t check your permissions right now. Please try again shortly.`
            : `The ${subject} couldn’t be loaded. Please try again.`,
        actions: retry ? [{
            label: 'Try again',
            run: () => { void retry().catch(error => showLoadError(error, target, retry)); },
        }, { label: 'Close' }] : [{ label: 'OK' }],
    });
}

let editor: Editor;

function filterInputUpdate(ev: Event) {
    const target = ev.target as HTMLInputElement;
    const filter = target.getAttribute('x-filter');
    // @ts-ignore
    filters[filter!] = target.value;
    editor?.updateList(filters).catch(error => showLoadError(error, 'list', () => editor.updateList(filters, true)));
}

const filterInputs = document.querySelectorAll('#filters > input');
filterInputs.forEach(elem => {
    const input = elem as HTMLInputElement;
    input.addEventListener('change', filterInputUpdate);
    input.addEventListener('keypress', filterInputUpdate);
    input.addEventListener('keyup', filterInputUpdate);
});

/**
 * Convert a string to upper cammel case. For example:
 *
 * alreadyUpperCamel -> AlreadyUpperCamel
 * lowerCammelCase -> LowerCammelCase
 * snake_case -> SnakeCase
 * With some spaces -> WithSomeSpaces
 * Part of LGBTQ -> PartOfLGBTQ
 */
function toUpperCammelCase(s: string): string {
    // First, remove surrounding whitespace
    s = s.trim();
    // The output starts with the first character, lower cased
    let output = s[0].toUpperCase();
    // Then we iterate over the remaining characters
    let isFollowingSpace = false;
    for (const ch of s.substring(1)) {
        switch (ch) {
            case '_':
            case '-':
            case ' ':
            case '\t':
            case '\r':
            case '\n':
                isFollowingSpace = true;
                break;
            default:
                if (isFollowingSpace) {
                    output += ch.toUpperCase();
                    isFollowingSpace = false;
                } else
                    output += ch;
        }
    }
    return output;
}

const args = parseSearchArgs();

if (args.sat) {
    // Single-access token flow
    // First, we remove the token from the URL
    history.replaceState(null, "", "?");

    // We then parse the token, which has the form:
    // <store-token-id>.<token:jwt>
    const tokenParts = args.sat.split(".");
    const storeTokenId = tokenParts[0];
    const token = tokenParts.slice(1).join(".");
    // We then start the fetch for the store token from simple-store. This is what makes the token
    // single-use. The store will only give us the token from the ID once.
    // Note that we don't await it here!
    const storeTokenPromise = fetch("https://simple-store.mevitae.net/api/get", {
        method: "POST",
        headers: {
            "X-MeVitae-Key": "public-editor-us",
        },
        body: storeTokenId,
        mode: "cors",
        cache: "no-store",
        redirect: "error",
    }).then(resp => {
        if (resp.status === 404) alert("Expired token, please use a new link.");
        if (!resp.ok) throw new Error("simple-store response not ok");
        return resp.text();
    }).then(token => {
        const successPopup = document.getElementById('success-popup');
        if (successPopup) {
            const {
                integration_settings_id: integrationSettingsId,
                success_message: successMessage,
                success_button_text: successButtonText,
                success_button_action: successButtonAction,
            } = JSON.parse(atob(token.split(".")[1].replaceAll("-", "+").replaceAll("_", "/")));
            configureCloseTab(Number(integrationSettingsId));
            if (successMessage) {
                const messagePara = successPopup.querySelector("#success-message");
                if (messagePara)
                    (messagePara as HTMLParagraphElement).innerText = successMessage;
                else console.error("success-message element does not exist");
            }
            const button = successPopup.querySelector("button.close-overlay");
            if (button) {
                if (successButtonText)
                    (button as HTMLButtonElement).innerText = successButtonText;
                if (successButtonAction) {
                    button.removeEventListener("click", closeOverlays);
                    button.addEventListener("click", () => location.href = successButtonAction);
                }
            } else console.error("success-popup > button does not exist");
        } else console.error("success-popup element does not exist");
        return token;
    });

    /**
     * Make a fetch request, with SAT credentials.
     */
    async function satFetch(
        url: string,
        options: RequestInit = {},
        retries = 1,
        timeout = 90000,
        refresh = true,
    ): Promise<Response> {
        const storeToken = await storeTokenPromise;
        if (!options) options = {};

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        options.signal = controller.signal;

        if (!options.headers) options.headers = {};
        // @ts-ignore
        options.headers['X-MeVitae-Store-Token'] = storeToken;
        // @ts-ignore
        options.headers['X-MeVitae-Single-Access-Token'] = token;

        const resp = await fetch(url, options);
        clearTimeout(timeoutId);
        if (resp.status === 401) {
            alert("unauthorized");
            navigateToLogin();
        }
        else if (resp.status !== 403 && !resp.ok && retries > 0) return satFetch(url, options, retries - 1, timeout, refresh);
        return resp;
    }

    editor = new Editor(satFetch, ui, false);

    hideSidebar(true);
    async function load() {
        await editor.singleAccess();
        // If selected, give is 500ms to load before fading in (and time for the hidden buttons to fade out)
        await sleep(500)
        ui.smallMenu.div.classList.remove('hidden');
        showPage();
    }
    load().catch(error => { showPage(); showLoadError(error, 'document', load); });
} else {
    async function startSso() {
        const incomingUrl = new URL(location.href);
        const auth = AsyncAuthWrapper.fromSession(navigateToLogin);
        const token = await auth.token();
        configureCloseTab(Number(token.parsedServiceToken["mv-isid"]));
        restoreLoginDestination(incomingUrl);
        const args = parseSearchArgs();
        editor = new Editor(
            auth.fetch,
            ui,
            !(args.job_id && args.candidate_id && args.doc_type && !isNaN(Number(args.doc_type))),
        );

        // The query parameters are used to set the default filters, and optionally auto-select a
        // document
        for (const key in args) {
            const filter = toUpperCammelCase(key);
            for (const input of filterInputs) {
                if (input.getAttribute('x-filter') === filter) {
                    const value = args[key];
                    (input as HTMLInputElement).value = value;
                    // @ts-ignore
                    filters[filter] = value;
                    break;
                }
            }
        }
        if (args.job_id && args.candidate_id && args.doc_type && !isNaN(Number(args.doc_type))) {
            ui.smallMenu.div.classList.add('hidden-smooth');
            ui.smallMenu.div.classList.add('hidden');
            const loadDocument = async () => {
                const selected = await editor.autoselect(args.job_id, args.candidate_id, Number(args.doc_type), args.job || "a");
                if (!selected) {
                    documentUnavailable('Document not found', 'The requested candidate or document could not be found. Open the candidate list to choose another document.');
                }
            };
            try {
                await loadDocument();
            } catch (error) {
                showLoadError(error, 'document', loadDocument);
            } finally {
                // Keep the list button available even when the direct load fails.
                await hideSidebar();
                ui.smallMenu.div.classList.remove('hidden', 'hidden-smooth');
            }
        } else {
            try {
                await editor.updateList(filters);
            } catch (error) {
                showLoadError(error, 'list', () => editor.updateList(filters, true));
            }
            showPage();
        }
    }
    startSso().catch(error => { showPage(); showLoadError(error); });
}
